import { useCallback, useEffect, useMemo, useState } from "react";
import {
  PolicyKind,
  PolicyStatus,
  WorkPolicyApi,
  WorkPolicySummary,
  WorkType
} from "../../../api/work-policy-api.types";

export interface PolicyCatalogFilters {
  keyword: string;
  workType: "ALL" | WorkType;
  status: "ALL" | PolicyStatus | "VISIBLE" | "HIDDEN";
}

const EMPTY_FILTERS: PolicyCatalogFilters = {
  keyword: "",
  workType: "ALL",
  status: "ALL"
};

export function getCatalogStatus(summary: WorkPolicySummary): PolicyStatus {
  if (summary.policyKind !== "APPROVAL") return summary.status;
  return (summary.approvalInUseCount ?? 0) > 0 ? "ACTIVE" : "INACTIVE";
}

export interface PolicyCatalogError {
  code: string;
  message: string;
  retryable: boolean;
}

function catalogError(error: unknown): PolicyCatalogError {
  const code =
    typeof error === "object" && error && "code" in error
      ? String((error as { code?: unknown }).code ?? "")
      : "";
  if (code === "WORK_POLICY_FORBIDDEN") {
    return {
      code,
      message:
        "근무 유형 관리를 열려면 근무제 설정 권한이 필요합니다. 접근 가능한 환경설정 화면으로 돌아가세요.",
      retryable: false
    };
  }
  if (code === "WORK_POLICY_DB_DEPENDENCY") {
    return {
      code,
      message:
        "근무 유형 기능의 DB 준비 상태를 운영자에게 확인해야 합니다. 입력이나 기존 데이터를 변경하지 않았습니다.",
      retryable: false
    };
  }
  return {
    code: code || "WORK_POLICY_LIST_FAILED",
    message: "정책 목록을 불러오지 못했습니다. 잠시 후 다시 시도하세요.",
    retryable: true
  };
}

export function useWorkPolicyCatalog(
  api: WorkPolicyApi,
  initialKind: PolicyKind = "APPROVAL"
) {
  const [policyKind, setPolicyKind] = useState<PolicyKind>(initialKind);
  const [summaries, setSummaries] = useState<WorkPolicySummary[]>([]);
  const [filters, setFilters] = useState<PolicyCatalogFilters>(EMPTY_FILTERS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<PolicyCatalogError | null>(null);

  const load = useCallback(
    async (kind: PolicyKind = policyKind) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.list({ policyKind: kind });
        setSummaries(response.data);
      } catch (caught) {
        setSummaries([]);
        setError(catalogError(caught));
      } finally {
        setLoading(false);
      }
    },
    [api, policyKind]
  );

  useEffect(() => {
    void load(policyKind);
  }, [load, policyKind]);

  const switchKind = useCallback((kind: PolicyKind) => {
    setPolicyKind(kind);
    setFilters(EMPTY_FILTERS);
  }, []);

  const filteredSummaries = useMemo(() => {
    const keyword = filters.keyword.trim().toLocaleLowerCase();
    return summaries.filter(summary => {
      if (
        keyword &&
        !summary.profileName.toLocaleLowerCase().includes(keyword)
      ) {
        return false;
      }
      if (filters.workType !== "ALL" && summary.workType !== filters.workType) {
        return false;
      }
      if (filters.status !== "ALL") {
        if (
          filters.status === "VISIBLE" ||
          filters.status === "HIDDEN"
        ) {
          if (summary.exposureStatus !== filters.status) return false;
        } else if (getCatalogStatus(summary) !== filters.status) {
          return false;
        }
      }
      return true;
    });
  }, [filters, summaries]);

  return {
    policyKind,
    summaries,
    filteredSummaries,
    filters,
    loading,
    error,
    filtersActive:
      filters.keyword !== "" ||
      filters.workType !== "ALL" ||
      filters.status !== "ALL",
    setFilters,
    resetFilters: () => setFilters(EMPTY_FILTERS),
    switchKind,
    refresh: load
  };
}
